import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { FileUploadInput } from '@/components/shared';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';

interface MedicalExamsFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
}

const MedicalExamsFieldset: React.FC<MedicalExamsFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Estado local para os dados do formulário (necessário para o useFileUpload)
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // File upload hook
    const { uploadStates, handleFileChange } = useFileUpload(setFormData);

    // Sincronizar mudanças do React Hook Form com o estado local
    useEffect(() => {
        const subscription = watch((value) => {
            setFormData(value as CollaboratorFormData);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // Obter valores específicos do React Hook Form
    const watchedAdmissionExamUrl = watch('admissionExamUrl');
    const watchedTerminationExamUrl = watch('terminationExamUrl');
    const watchedAdmissionExamFile = watch('admissionExamFile');
    const watchedTerminationExamFile = watch('terminationExamFile');

    // URLs para os campos de upload (prioriza dados do formulário)
    const admissionExamUrl = watchedAdmissionExamUrl || formData.admissionExamUrl;
    const terminationExamUrl = watchedTerminationExamUrl || formData.terminationExamUrl;

    // useEffect para atualizar URLs quando mudarem
    useEffect(() => {
        if (admissionExamUrl) {
            setValue('admissionExamUrl', admissionExamUrl);
        }
    }, [admissionExamUrl, setValue]);

    useEffect(() => {
        if (terminationExamUrl) {
            setValue('terminationExamUrl', terminationExamUrl);
        }
    }, [terminationExamUrl, setValue]);

    // Helper local para obter layout contextual
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        switch (context) {
            case 'DP_UPDATE':
            case 'DHO_UPDATE':
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
            default: // FULL_FORM
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
        }
    };

    const layout = getLayoutByContext(context);

    // Verificar se o fieldset deve ser exibido
    if (!shouldShow('admissionExamFile') && !shouldShow('terminationExamFile')) {
        return null;
    }

    return (
        <fieldset className={layout.fieldsetStyle}>
            <legend className={layout.legendStyle}>Exames Médicos</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                {shouldShow('admissionExamFile') && (
                    <Controller
                        name="admissionExamFile"
                        control={control}
                        render={({ field, fieldState }) => (
                            <div>
                                <FileUploadInput
                                    name="admissionExamFile"
                                    label="Exame Admissional"
                                    fileUrl={admissionExamUrl || ''}
                                    fileName={watchedAdmissionExamFile?.name || ''}
                                    isSubmitting={isSubmitting}
                                    progress={uploadStates['admissionExamFile']?.progress}
                                    status={uploadStates['admissionExamFile']?.status || 'idle'}
                                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;

                                        field.onChange(file);
                                        
                                        // Upload do arquivo e atualização do URL no React Hook Form
                                        try {
                                            const downloadURL = await handleFileChange(e, {
                                                fieldName: 'admissionExamFile',
                                                urlFieldName: 'admissionExamUrl',
                                                pathPrefix: 'medical-exams',
                                                id: collaboratorId
                                            });
                                            
                                            // Atualizar React Hook Form com a URL
                                            if (downloadURL) {
                                                setValue('admissionExamUrl', downloadURL);
                                            }
                                        } catch (error) {
                                            console.error('Erro no upload do exame admissional:', error);
                                        }
                                    }}
                                />
                                {fieldState.error && (
                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                        {fieldState.error.message}
                                    </p>
                                )}
                            </div>
                        )}
                    />
                )}

                {shouldShow('terminationExamFile') && (
                    <Controller
                        name="terminationExamFile"
                        control={control}
                        render={({ field, fieldState }) => (
                            <div>
                                <FileUploadInput
                                    name="terminationExamFile"
                                    label="Exame Demissional"
                                    fileUrl={terminationExamUrl || ''}
                                    fileName={watchedTerminationExamFile?.name || ''}
                                    isSubmitting={isSubmitting}
                                    progress={uploadStates['terminationExamFile']?.progress}
                                    status={uploadStates['terminationExamFile']?.status || 'idle'}
                                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;

                                        field.onChange(file);
                                        
                                        // Upload do arquivo e atualização do URL no React Hook Form
                                        try {
                                            const downloadURL = await handleFileChange(e, {
                                                fieldName: 'terminationExamFile',
                                                urlFieldName: 'terminationExamUrl',
                                                pathPrefix: 'medical-exams',
                                                id: collaboratorId
                                            });
                                            
                                            // Atualizar React Hook Form com a URL
                                            if (downloadURL) {
                                                setValue('terminationExamUrl', downloadURL);
                                            }
                                        } catch (error) {
                                            console.error('Erro no upload do exame demissional:', error);
                                        }
                                    }}
                                />
                                {fieldState.error && (
                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                        {fieldState.error.message}
                                    </p>
                                )}
                            </div>
                        )}
                    />
                )}
            </div>
        </fieldset>
    );
};

export default MedicalExamsFieldset; 